import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaClock,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt
} from 'react-icons/fa';

import './RajasthanRoyal.css';

const RajasthanRoyal = () => {

  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: 'smooth'
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Jaipur",
      points: [
        "Arrival in Jaipur, the capital city of Rajasthan also known as the Pink City.",
        "After hotel check-in proceed for local sightseeing.",
        "Places to visit: City Palace, Jantar Mantar Observatory, and Hawa Mahal (photo stop).",
        "In the evening explore Johari Bazaar and Bapu Bazaar famous for handicrafts, jewelry, textiles, and traditional Rajasthani items.",
        "Dinner and overnight stay in Jaipur."
      ],
      icon: <FaBus />
    },

    {
      day: 2,
      title: "Jaipur Sightseeing",
      points: [
        "After breakfast continue exploring Jaipur’s famous landmarks.",
        "Visit Amber Fort – magnificent hilltop fort accessible by jeep ride.",
        "Visit Jal Mahal (photo stop).",
        "Visit Nahargarh Fort viewpoint offering panoramic views of Jaipur city.",
        "Later enjoy leisure time exploring Jaipur markets and local cafés.",
        "Dinner and overnight stay in Jaipur."
      ],
      icon: <FaMapMarkerAlt />
    },

    {
      day: 3,
      title: "Jaipur to Jodhpur",
      points: [
        "After breakfast drive to Jodhpur, known as the Blue City.",
        "Approx. Distance: 330 km.",
        "Estimated Travel Time: 6 hours.",
        "Upon arrival visit Mehrangarh Fort – one of the largest forts in India.",
        "Visit Jaswant Thada – beautiful marble memorial.",
        "Explore Clock Tower Market popular for handicrafts and spices.",
        "Dinner and overnight stay in Jodhpur."
      ],
      icon: <FaGlobeAmericas />
    },

    {
      day: 4,
      title: "Jodhpur to Jaisalmer",
      points: [
        "After breakfast proceed to Jaisalmer, the famous Golden City.",
        "Approx. Distance: 280 km.",
        "Estimated Travel Time: 5 hours.",
        "Visit Jaisalmer Fort – one of the few living forts in the world.",
        "Explore Patwon Ki Haveli – historic merchant mansion.",
        "Visit Gadisar Lake – scenic reservoir surrounded by temples.",
        "Dinner and overnight stay in Jaisalmer."
      ],
      icon: <FaBus />
    },

    {
      day: 5,
      title: "Jaisalmer Desert Experience",
      points: [
        "After breakfast enjoy sightseeing in Jaisalmer.",
        "Later proceed to Sam Sand Dunes.",
        "Approx. Distance: 45 km.",
        "Enjoy camel safari across the golden sand dunes.",
        "Witness beautiful sunset views over the Thar Desert.",
        "Enjoy traditional Rajasthani folk dance and cultural performances at desert camp.",
        "Dinner and overnight stay in Desert Camp."
      ],
      icon: <FaStar />
    },

    {
      day: 6,
      title: "Jaisalmer to Udaipur",
      points: [
        "After breakfast drive to Udaipur, the beautiful City of Lakes.",
        "Approx. Distance: 500 km.",
        "Estimated Travel Time: 9 hours.",
        "Upon arrival check in to the hotel.",
        "Evening free to explore the Lake Pichola area and local markets.",
        "Dinner and overnight stay in Udaipur."
      ],
      icon: <FaBus />
    },

    {
      day: 7,
      title: "Udaipur Sightseeing & Departure",
      points: [
        "After breakfast proceed for sightseeing in Udaipur.",
        "Places to visit: City Palace, Jagdish Temple, Saheliyon Ki Bari, and Fateh Sagar Lake.",
        "Optional boat ride on Lake Pichola.",
        "Later transfer for departure.",
        "Tour concludes with beautiful memories of Rajasthan’s royal heritage."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (

    <div className="trip-container">

      {/* HEADER */}

      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >

        <div className="header-left">

          <h1 className="trip-title">
            Rajasthan Royal Heritage Journey
          </h1>

          <p className="trip-duration">
            Jaipur – Jodhpur – Jaisalmer – Udaipur
          </p>

        </div>

        <div className="header-right">

          <div className="info-row">

            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>
              <p>Jaipur, Jodhpur, Jaisalmer, Udaipur</p>
            </div>

          </div>

          <div className="info-row">

            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Service Type:</span>
              <p>Private & Personalized</p>
            </div>

          </div>

          <div className="info-row type-tag">

            <FaGlobeAmericas className="header-icon" />

            <p>Royal Heritage Tour</p>

          </div>

        </div>

      </motion.header>

      {/* IMAGE GRID */}

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.unsplash.com/photo-1477587458883-47145ed94245?q=80&w=1170&auto=format&fit=crop"
            alt="Jaipur"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://plus.unsplash.com/premium_photo-1661963629241-52c812d5c7f8?q=80&w=387&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Jodhpur"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1654245363109-b873bb61b2fa?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Jaisalmer"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1575994532957-773da2f83eb1?q=80&w=771&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Udaipur"
            />
          </div>

        </div>

      </motion.div>

      {/* STICKY NAV */}

      <nav className="sticky-nav">

        <button
          className="tab-link"
          onClick={() => scrollToSection(itineraryRef)}
        >
          Detailed Itinerary
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(highlightsRef)}
        >
          Highlights
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(inclusionsRef)}
        >
          Policy & Inclusions
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(aboutRef)}
        >
          About Trip
        </button>

      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}

        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Tour Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (

              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">
                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>
                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (

                    <div key={i} className="point-item">

                      <FaChevronRight className="bullet-icon" />

                      <p>{p}</p>

                    </div>

                  ))}

                </div>

              </motion.div>

            ))}

          </div>

        </section>

        {/* HIGHLIGHTS */}

        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Explore the majestic Amber Fort and royal palaces of Jaipur</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit the magnificent Mehrangarh Fort in Jodhpur</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Discover the golden architecture of Jaisalmer Fort and historic havelis</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Enjoy a camel safari and desert sunset experience at Sam Sand Dunes</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Experience traditional Rajasthani folk dance and cultural performances</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Explore the romantic lakes and palaces of Udaipur – the City of Lakes</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit vibrant Rajasthani bazaars and traditional markets</p>
            </div>

          </div>

        </motion.section>

        {/* INCLUSIONS */}

        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaCheckCircle />
              What's Included
            </h4>

            <ul className="p-list">

              <li><FaHotel /> Accommodation in comfortable hotels as per itinerary</li>

              <li><FaClock /> 6 nights stay as per itinerary</li>

              <li><FaUtensils /> Daily breakfast and dinner at hotels/camps</li>

              <li><FaBus /> Transportation and transfers throughout the tour</li>

              <li><FaCheckCircle /> Sightseeing as mentioned in the itinerary</li>

              <li><FaStar /> Camel safari and cultural program at Sam Sand Dunes</li>

              <li><FaShieldAlt /> Driver charges, fuel, toll taxes and parking</li>

              <li><FaCheckCircle /> Tour coordination and assistance</li>

            </ul>

          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">
              <FaTimesCircle />
              What's Excluded
            </h4>

            <ul className="p-list">

              <li><FaTimesCircle /> Airfare or train tickets</li>

              <li><FaTimesCircle /> Entry tickets to monuments or attractions</li>

              <li><FaTimesCircle /> Jeep ride charges at Amber Fort if applicable</li>

              <li><FaTimesCircle /> Camera or video charges at monuments</li>

              <li><FaTimesCircle /> Personal expenses such as shopping, tips, or laundry</li>

              <li><FaShieldAlt /> Travel insurance and medical expenses</li>

              <li><FaTimesCircle /> Early check-in or late check-out at hotels</li>

              <li><FaTimesCircle /> Any additional sightseeing not mentioned in the itinerary</li>

              <li><FaTimesCircle /> Any cost arising due to weather conditions or unforeseen circumstances</li>

            </ul>

          </div>

        </motion.section>

        {/* ABOUT */}

        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">

            Experience the royal grandeur of Rajasthan through its magnificent forts, golden desert landscapes, vibrant bazaars, and romantic lakes.

            <br /><br />

            The Rajasthan Royal Heritage Journey takes travelers across the state’s most iconic destinations including Jaipur, Jodhpur, Jaisalmer, and Udaipur.

            <br /><br />

            From exploring the majestic Amber Fort in Jaipur to witnessing the golden sand dunes of Jaisalmer and enjoying the serene lakes of Udaipur, this journey showcases the rich heritage, culture, and architectural brilliance of Rajasthan.

            <br /><br />

            Travelers will also experience the unique desert lifestyle with a camel safari and cultural evening in the Thar Desert.

            <br /><br />

            With scenic drives, historic monuments, and immersive cultural experiences, this tour offers a complete exploration of Rajasthan’s royal legacy.

          </p>

        </motion.section>

        {/* TERMS */}

        <div className="accordion-item">

          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: 'rotate(-90deg)' }}
            />

          </Link>

        </div>

        {/* PRIVACY */}

        <div className="accordion-item">

          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer'
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: 'rotate(-90deg)' }}
            />

          </Link>

        </div>

        {/* NOTE */}

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section travel-info-grid"
        >

          <div
            className="info-card note-card"
            style={{ width: '100%' }}
          >

            <h4 className="info-card-title">
              <FaPaperPlane />
              Important Note
            </h4>

            <p>

              Flight and train tickets can be arranged by
              <strong> Chandrama Holidays </strong>
              on request at the best available fares.

              <br /><br />

              Our team will assist you in planning convenient travel arrangements for a smooth and comfortable Rajasthan journey.

            </p>

          </div>

        </motion.section>

      </div>

    </div>
  );
};

export default RajasthanRoyal;